import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PrintIcon from '@mui/icons-material/Print';
import ShareIcon from '@mui/icons-material/Share';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import { exportQuotationToPdf, shareQuotationPdf } from '../utils/exportQuotationPdf';
import { useGetQuotationsQuery } from '../../../store/api/quotationsApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';

function QuotationsList() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetQuotationsQuery({ limit: 50 });
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency , minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2), maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)}).format(amount);
    } catch (e) {
      return `${currency} ${amount.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}`;
    }
  };

  const getStatusColor = (status) => {
    const colors = { draft: 'default', sent: 'info', accepted: 'success', rejected: 'error', converted: 'success' };
    return colors[status] || 'default';
  };

  if (isLoading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">Error loading quotations</Typography>;

  const quotations = data?.quotations || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Quotations
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/quotations/new')}
          sx={{ borderRadius: 1 }}
        >
          New Quotation
        </Button>
      </Box>

      <Card sx={{ borderRadius: 1 }}>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Quotation #</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Valid Until</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{`Total (${currency})`}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {quotations.length > 0 ? (
                  quotations.map((q) => (
                    <TableRow key={q.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{q.quotationNumber}</TableCell>
                      <TableCell>{q.customer?.name || '-'}</TableCell>
                      <TableCell>{q.quotationDate ? new Date(q.quotationDate).toLocaleDateString() : '-'}</TableCell>
                      <TableCell>{q.validUntil ? new Date(q.validUntil).toLocaleDateString() : '-'}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'success.main' }}>{formatAmount(q.grandTotal)}</TableCell>
                      <TableCell>
                        <Chip
                          label={q.status}
                          color={getStatusColor(q.status)}
                          size="small"
                          sx={{ textTransform: 'capitalize', fontWeight: 600, borderRadius: 1 }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" color="primary" onClick={() => navigate(`/quotations/${q.id}`)} title="View Details">
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        {q.status !== 'converted' && q.status !== 'rejected' && (
                          <IconButton
                            size="small"
                            color="success"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/sales/orders/new?quotationId=${q.id}`);
                            }}
                            title="Convert to Sales Order Form"
                          >
                            <PointOfSaleIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            exportQuotationToPdf(q, company);
                          }}
                          title="Print/Download PDF"
                        >
                          <PrintIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="info"
                          onClick={(e) => {
                            e.stopPropagation();
                            shareQuotationPdf(q, company);
                          }}
                          title="Share"
                        >
                          <ShareIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No quotations found</Typography>
                      <Button variant="outlined" startIcon={<AddIcon />} onClick={() => navigate('/quotations/new')} sx={{ mt: 2, borderRadius: 1 }}>
                        Create First Quotation
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}

export default QuotationsList;
